import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LANGUAGES, changeLanguage } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkles, Zap, Coins as CoinsIcon, Users, ArrowRight, Globe, Check,
  ShieldCheck, ChevronDown, MapPin, Calendar, Heart, MessageSquare,
  Camera, Crown, Clock, Lock, CheckCircle2, Star
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1582298538104-fe2e74c27f59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwaGFuZ2luZyUyMG91dCUyMGxhdWdoaW5nJTIwY29mZmVlfGVufDB8fHx8MTc4Mjg0NjcyNnww&ixlib=rb-4.1.0&q=85";

function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{
        transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.8,.2,1)",
        transitionDelay: `${delay}ms`,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(24px)",
      }}
      className={className}
    >
      {children}
    </div>
  );
}

function LangPicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  return (
    <div className="relative" data-testid="landing-lang-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-ink bg-white shadow-nb-sm hover:-translate-y-0.5 transition-transform text-sm font-display font-bold"
        data-testid="landing-lang-toggle"
      >
        <Globe className="w-4 h-4 text-ink" strokeWidth={2.5} />
        <span className="text-base leading-none">{current.flag}</span>
        <span className="hidden sm:inline">{current.label}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute end-0 mt-2 w-56 bg-white border-2 border-ink rounded-2xl shadow-nb z-50 p-2 max-h-[70vh] overflow-y-auto">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => { changeLanguage(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-[#F0EFE7] transition-colors ${i18n.language === l.code ? "bg-[#E8F7F2]" : ""}`}
                data-testid={`landing-lang-${l.code}`}
                dir={l.dir}
              >
                <span className="text-lg leading-none">{l.flag}</span>
                <span className="font-medium flex-1 text-start">{l.label}</span>
                {i18n.language === l.code && <Check className="w-4 h-4 text-[#14C8B0]" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState("all");

  // Auto-redirect signed-in users to feed
  useEffect(() => {
    if (user) nav("/feed", { replace: true });
  }, [user, nav]);

  // Comprehensive 11 app features list
  const allFeatures = [
    {
      id: "spontaneous",
      category: "hangouts",
      icon: Zap,
      tint: "#FFE08A",
      title: t("landing.features.spontaneous.title"),
      body: t("landing.features.spontaneous.body"),
      tag: t("landing.features.spontaneous.tag"),
      previewType: "status"
    },
    {
      id: "liveMap",
      category: "hangouts",
      icon: MapPin,
      tint: "#14C8B0",
      title: t("landing.features.liveMap.title"),
      body: t("landing.features.liveMap.body"),
      tag: t("landing.features.liveMap.tag"),
      previewType: "map"
    },
    {
      id: "activityHosting",
      category: "hangouts",
      icon: Calendar,
      tint: "#BFE9D6",
      title: t("landing.features.activityHosting.title"),
      body: t("landing.features.activityHosting.body"),
      tag: t("landing.features.activityHosting.tag"),
      previewType: "activities"
    },
    {
      id: "vibeMatch",
      category: "social",
      icon: Heart,
      tint: "#FF7A6B",
      title: t("landing.features.vibeMatch.title"),
      body: t("landing.features.vibeMatch.body"),
      tag: t("landing.features.vibeMatch.tag"),
      previewType: "swipe"
    },
    {
      id: "chats",
      category: "social",
      icon: MessageSquare,
      tint: "#D9CFFF",
      title: t("landing.features.chats.title"),
      body: t("landing.features.chats.body"),
      tag: t("landing.features.chats.tag"),
      previewType: "chat"
    },
    {
      id: "moments",
      category: "social",
      icon: Camera,
      tint: "#FFE6E0",
      title: t("landing.features.moments.title"),
      body: t("landing.features.moments.body"),
      tag: t("landing.features.moments.tag"),
      previewType: "moments"
    },
    {
      id: "tCoins",
      category: "economy",
      icon: CoinsIcon,
      tint: "#F9C349",
      title: t("landing.features.tCoins.title"),
      body: t("landing.features.tCoins.body"),
      tag: t("landing.features.tCoins.tag"),
      previewType: "coins"
    },
    {
      id: "vipShop",
      category: "economy",
      icon: Crown,
      tint: "#FFF1D6",
      title: t("landing.features.vipShop.title"),
      body: t("landing.features.vipShop.body"),
      tag: t("landing.features.vipShop.tag"),
      previewType: "vip"
    },
    {
      id: "calendar",
      category: "safety",
      icon: Clock,
      tint: "#E2F1F8",
      title: t("landing.features.calendar.title"),
      body: t("landing.features.calendar.body"),
      tag: t("landing.features.calendar.tag"),
      previewType: "timeline"
    },
    {
      id: "safetyId",
      category: "safety",
      icon: ShieldCheck,
      tint: "#C8F0E8",
      title: t("landing.features.safetyId.title"),
      body: t("landing.features.safetyId.body"),
      tag: t("landing.features.safetyId.tag"),
      previewType: "trust"
    },
    {
      id: "multiLang",
      category: "safety",
      icon: Globe,
      tint: "#F0EFE7",
      title: t("landing.features.multiLang.title"),
      body: t("landing.features.multiLang.body"),
      tag: t("landing.features.multiLang.tag"),
      previewType: "languages"
    }
  ];

  const categoryTabs = [
    { id: "all", label: t("landing.explorer.tabs.all") },
    { id: "hangouts", label: t("landing.explorer.tabs.hangouts") },
    { id: "social", label: t("landing.explorer.tabs.social") },
    { id: "economy", label: t("landing.explorer.tabs.economy") },
    { id: "safety", label: t("landing.explorer.tabs.safety") }
  ];

  const filteredFeatures = activeCategory === "all"
    ? allFeatures
    : allFeatures.filter((f) => f.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#F7F7F2] text-[#0A0B0D] overflow-x-hidden font-sans">
      {/* ============== TOP BAR ============== */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#F7F7F2]/85 border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-9 h-9 rounded-xl bg-[#14C8B0] border-2 border-ink shadow-nb-sm flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.8} />
            </div>
            <span className="font-display font-black text-2xl tracking-tight">TANTA</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <LangPicker />
            <Link
              to="/auth"
              className="hidden sm:inline-flex btn-nb btn-nb-dark text-sm"
              data-testid="landing-login-btn"
            >
              {t("auth.login")}
            </Link>
            <Link
              to="/auth?mode=signup"
              className="btn-nb btn-nb-primary text-sm"
              data-testid="landing-signup-btn"
            >
              {t("landing.cta.joinNow")}
            </Link>
          </div>
        </div>
      </header>

      {/* ============== HERO ============== */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-16 pb-16 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left column — text */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border-2 border-ink shadow-nb-sm">
                <span className="w-2.5 h-2.5 rounded-full bg-[#14C8B0] animate-pulse" />
                <span className="font-display font-bold text-xs tracking-widest uppercase text-ink">
                  {t("landing.hero.kicker")}
                </span>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="mt-5 font-display font-black tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl xl:text-[84px]">
                {t("landing.hero.titleLine1")}
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10">{t("landing.hero.titleLine2")}</span>
                  <span aria-hidden className="absolute inset-x-0 bottom-2 lg:bottom-3 h-3 lg:h-5 bg-[#14C8B0] -z-0 rotate-[-1deg]" />
                </span>
                <br />
                <span className="text-[#FF7A6B]">{t("landing.hero.titleLine3")}</span>
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 text-base sm:text-lg text-[#3D424B] leading-relaxed max-w-xl">
                {t("landing.hero.subtitle")}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/auth?mode=signup"
                  className="btn-nb btn-nb-primary text-base px-6 py-3.5 inline-flex items-center gap-2"
                  data-testid="landing-hero-signup-btn"
                >
                  {t("landing.cta.startFree")}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/auth"
                  className="btn-nb btn-nb-coral text-base px-6 py-3.5"
                  data-testid="landing-hero-login-btn"
                >
                  {t("auth.login")}
                </Link>
                <span className="font-script text-2xl text-[#FF7A6B] rotate-[-3deg] -ms-1 -mt-1">
                  {t("landing.hero.note")}
                </span>
              </div>
            </Reveal>
            <Reveal delay={400}>
              <div className="mt-8 flex items-center gap-4 text-sm text-[#3D424B]">
                <div className="flex -space-x-2">
                  {["#FFE6E0", "#DDF5EA", "#FFF1D6", "#ECE6FF"].map((c, i) => (
                    <div key={i} className="w-9 h-9 rounded-full border-2 border-ink flex items-center justify-center font-display font-black text-xs" style={{ background: c }}>
                      {["M", "L", "K", "A"][i]}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="font-display font-extrabold text-[#0A0B0D]">{t("landing.hero.socialCount")}</div>
                  <div className="text-xs">{t("landing.hero.socialBlurb")}</div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right column — image & live badge preview */}
          <div className="lg:col-span-5 relative h-[420px] sm:h-[500px] lg:h-[560px]">
            <img
              src={HERO_IMG}
              alt="TANTA Community Hangout"
              className="absolute top-0 end-0 w-[88%] h-[80%] object-cover rounded-2xl border-2 border-ink shadow-nb"
            />
            <div
              className="absolute bottom-2 start-0 w-[72%] bg-white border-2 border-ink rounded-2xl shadow-nb p-4 animate-float z-10"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-full bg-[#FF7A6B] border-2 border-ink flex items-center justify-center text-white font-bold text-sm">
                  M
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-display font-extrabold text-sm text-ink">Maya · 24</span>
                    <CheckCircle2 className="w-4 h-4 text-[#14C8B0]" />
                  </div>
                  <div className="text-[10px] text-[#7C828D] uppercase tracking-wider">Ready Now ⚡ · 2 min ago</div>
                </div>
              </div>
              <div className="text-sm font-medium leading-snug text-[#0A0B0D]">
                {t("landing.hero.cardCopy")}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs text-[#3D424B] flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-[#14C8B0]" /> Södermalm
                </div>
                <span className="px-2.5 py-1 rounded-full bg-[#FFE08A] border-2 border-ink text-xs font-display font-bold">
                  {t("landing.hero.spontaneous")}
                </span>
              </div>
            </div>
            <div
              className="absolute top-6 end-6 px-3.5 py-2 rounded-full bg-[#14C8B0] border-2 border-ink shadow-nb-sm font-display font-extrabold text-white text-sm rotate-[-6deg] z-10 flex items-center gap-1.5"
            >
              <Sparkles className="w-4 h-4" />
              <span>{t("landing.hero.badge")}</span>
            </div>
          </div>
        </div>

        {/* Marquee Strip */}
        <div className="mt-14 lg:mt-20 -mx-4 sm:-mx-6 lg:-mx-10 border-y-2 border-ink bg-[#0A0B0D] text-white overflow-hidden">
          <div className="flex items-center gap-12 py-4 whitespace-nowrap animate-[marquee_28s_linear_infinite] font-display font-black uppercase tracking-widest text-lg sm:text-xl">
            {Array.from({ length: 2 }).map((_, group) => (
              <div key={group} className="flex items-center gap-12">
                <span>✦ {t("landing.marquee.spontaneous")}</span>
                <span className="text-[#14C8B0]">●</span>
                <span>📍 {t("landing.marquee.liveMap")}</span>
                <span className="text-[#FF7A6B]">●</span>
                <span>✦ {t("landing.marquee.cities")}</span>
                <span className="text-[#F9C349]">●</span>
                <span>🛡️ {t("landing.marquee.verified")}</span>
                <span className="text-[#14C8B0]">●</span>
                <span>🪙 {t("landing.marquee.coins")}</span>
                <span className="text-[#FF7A6B]">●</span>
                <span>📸 {t("landing.marquee.moments")}</span>
                <span className="text-[#F9C349]">●</span>
                <span>🌐 {t("landing.marquee.languages")}</span>
                <span className="text-[#14C8B0]">●</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== INTERACTIVE FEATURE EXPLORER ============== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Reveal>
            <span className="font-script text-3xl text-[#14C8B0]">{t("landing.explorer.kicker")}</span>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              {t("landing.explorer.headline")}
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-4 text-base sm:text-lg text-[#3D424B] leading-relaxed">
              {t("landing.explorer.subhead")}
            </p>
          </Reveal>

          {/* Category Explorer Filter Tabs */}
          <Reveal delay={240}>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id)}
                  data-testid={`landing-feature-tab-${tab.id}`}
                  className={`px-4 py-2 rounded-xl border-2 border-ink font-display font-bold text-sm transition-all ${
                    activeCategory === tab.id
                      ? "bg-[#0A0B0D] text-white shadow-nb-sm -translate-y-0.5"
                      : "bg-white text-[#0A0B0D] hover:bg-[#EFEEE6]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Bento Grid showcasing filtered features */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.id} delay={80 * (i % 6)}>
                <div className="card-nb bg-white p-6 lg:p-7 h-full flex flex-col justify-between hover:-translate-y-1 transition-transform">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div
                        className="w-12 h-12 rounded-xl border-2 border-ink flex items-center justify-center shadow-nb-sm"
                        style={{ background: f.tint }}
                      >
                        <Icon className="w-6 h-6 text-ink" strokeWidth={2.5} />
                      </div>
                      <span className="px-2.5 py-1 rounded-full border-2 border-ink text-[11px] font-display font-extrabold uppercase bg-[#F7F7F2]">
                        {f.tag}
                      </span>
                    </div>

                    <h3 className="font-display font-black text-2xl leading-tight text-ink">
                      {f.title}
                    </h3>
                    <p className="mt-2.5 text-sm text-[#3D424B] leading-relaxed">
                      {f.body}
                    </p>
                  </div>

                  {/* Interactive Mini-Previews inside cards */}
                  <div className="mt-6 border-2 border-ink rounded-xl bg-[#FFFDF7] p-3 shadow-nb-sm">
                    {f.previewType === "status" && (
                      <div className="flex items-center justify-between text-xs font-display font-bold">
                        <span className="flex items-center gap-1.5 text-[#14C8B0]">
                          <span className="w-2 h-2 rounded-full bg-[#14C8B0] animate-ping" />
                          Ready Now ⚡
                        </span>
                        <span className="bg-[#FFE08A] px-2 py-0.5 rounded-md border border-ink text-[10px]">
                          Nearby (3 min away)
                        </span>
                      </div>
                    )}

                    {f.previewType === "map" && (
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="flex items-center gap-1 text-ink">
                          <MapPin className="w-3.5 h-3.5 text-[#FF7A6B]" /> Live Location Sharing
                        </span>
                        <span className="text-[#14C8B0] font-bold">ACTIVE ●</span>
                      </div>
                    )}

                    {f.previewType === "activities" && (
                      <div className="flex gap-1.5 flex-wrap">
                        {["🏀 Sports", "☕ Social", "📚 Study", "🎉 Fun"].map((cat) => (
                          <span key={cat} className="px-2 py-0.5 rounded-lg border border-ink text-[11px] font-bold bg-white">
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}

                    {f.previewType === "swipe" && (
                      <div className="flex items-center justify-between text-xs font-display font-bold">
                        <span className="text-[#FF7A6B] flex items-center gap-1">
                          <Heart className="w-3.5 h-3.5 fill-[#FF7A6B]" /> Super Like
                        </span>
                        <span className="bg-[#D9CFFF] px-2 py-0.5 rounded border border-ink text-[10px]">
                          Vibe Score 98%
                        </span>
                      </div>
                    )}

                    {f.previewType === "chat" && (
                      <div className="flex items-center gap-2 text-xs">
                        <MessageSquare className="w-3.5 h-3.5 text-[#14C8B0]" />
                        <span className="font-medium text-ink truncate">Group chat active · 4 members online</span>
                      </div>
                    )}

                    {f.previewType === "moments" && (
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="flex items-center gap-1">
                          <Camera className="w-3.5 h-3.5 text-[#FF7A6B]" /> Photo Moment
                        </span>
                        <span className="text-[#7C828D]">Expires in 6d 22h</span>
                      </div>
                    )}

                    {f.previewType === "coins" && (
                      <div className="flex items-center justify-between text-xs font-display font-bold">
                        <span className="flex items-center gap-1 text-[#F9C349]">
                          <CoinsIcon className="w-4 h-4 fill-[#F9C349] text-ink" /> T-Coins Wallet
                        </span>
                        <span className="bg-[#14C8B0] text-white px-2 py-0.5 rounded border border-ink text-[10px]">
                          Stripe Cashout
                        </span>
                      </div>
                    )}

                    {f.previewType === "vip" && (
                      <div className="flex items-center justify-between text-xs font-display font-bold">
                        <span className="flex items-center gap-1 text-[#FF7A6B]">
                          <Crown className="w-4 h-4" /> TANTA VIP
                        </span>
                        <span className="text-[10px] text-[#7C828D]">Unlimited Swipes</span>
                      </div>
                    )}

                    {f.previewType === "timeline" && (
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-[#14C8B0]" /> Scheduled
                        </span>
                        <span className="font-bold text-ink">Today @ 18:00</span>
                      </div>
                    )}

                    {f.previewType === "trust" && (
                      <div className="flex items-center justify-between text-xs font-display font-bold">
                        <span className="flex items-center gap-1 text-[#14C8B0]">
                          <ShieldCheck className="w-4 h-4" /> Selfie ID Verified
                        </span>
                        <span className="bg-[#BFE9D6] px-1.5 py-0.5 rounded border border-ink text-[10px]">
                          Trust 100%
                        </span>
                      </div>
                    )}

                    {f.previewType === "languages" && (
                      <div className="flex items-center justify-between text-xs font-bold">
                        <span className="flex items-center gap-1">
                          <Globe className="w-3.5 h-3.5 text-[#14C8B0]" /> 10 Languages
                        </span>
                        <span className="font-mono text-[10px]">LTR + RTL</span>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ============== FEATURE SPOTLIGHT 1: ECONOMY & VIP ============== */}
      <section className="bg-[#0A0B0D] text-white py-20 lg:py-28 border-y-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="font-script text-3xl text-[#F9C349]">
                  {t("landing.economySection.kicker")}
                </span>
              </Reveal>
              <Reveal delay={100}>
                <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                  {t("landing.economySection.headline")}
                </h2>
              </Reveal>
              <Reveal delay={180}>
                <p className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl">
                  {t("landing.economySection.body")}
                </p>
              </Reveal>
              <Reveal delay={260}>
                <div className="mt-8 space-y-3">
                  {[
                    t("landing.economySection.perk1"),
                    t("landing.economySection.perk2"),
                    t("landing.economySection.perk3")
                  ].map((perk, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm sm:text-base">
                      <div className="w-6 h-6 rounded-full bg-[#14C8B0] border border-white flex items-center justify-center text-ink flex-shrink-0">
                        <Check className="w-4 h-4 stroke-[3]" />
                      </div>
                      <span>{perk}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={320}>
                <div className="mt-8">
                  <Link
                    to="/auth?mode=signup"
                    className="btn-nb btn-nb-primary text-base px-6 py-3.5 inline-flex items-center gap-2"
                    data-testid="landing-economy-signup-btn"
                  >
                    {t("landing.cta.joinNow")}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* Economy Cards Visual Stack */}
            <div className="lg:col-span-5 relative">
              <div className="bg-white text-ink border-2 border-ink rounded-2xl p-6 shadow-nb">
                <div className="flex items-center justify-between pb-4 border-b-2 border-ink">
                  <div className="flex items-center gap-2">
                    <CoinsIcon className="w-7 h-7 text-[#F9C349]" strokeWidth={2.5} />
                    <span className="font-display font-black text-xl">T-Coins Wallet</span>
                  </div>
                  <span className="px-3 py-1 bg-[#F9C349] border-2 border-ink rounded-full font-mono text-xs font-bold">
                    Balance: 1,250 🪙
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between text-sm p-3 rounded-xl bg-[#F7F7F2] border-2 border-ink">
                    <span className="font-bold">☕ Gift Matcha to Host</span>
                    <span className="text-[#FF7A6B] font-mono font-bold">-50 🪙</span>
                  </div>
                  <div className="flex items-center justify-between text-sm p-3 rounded-xl bg-[#F7F7F2] border-2 border-ink">
                    <span className="font-bold">🚀 Activity Post Boost</span>
                    <span className="text-[#FF7A6B] font-mono font-bold">-10 🪙</span>
                  </div>
                  <div className="flex items-center justify-between text-sm p-3 rounded-xl bg-[#E8F7F2] border-2 border-ink">
                    <span className="font-bold">💵 Stripe Bank Cashout</span>
                    <span className="text-[#14C8B0] font-mono font-bold">+$120.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="bg-[#EFEEE6] border-b-2 border-ink py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <Reveal>
              <span className="font-script text-3xl text-[#FF7A6B]">{t("landing.how.kicker")}</span>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                {t("landing.how.headline")}
              </h2>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10 relative">
            {[1, 2, 3].map((n, i) => (
              <Reveal key={n} delay={150 * i}>
                <div className="card-nb bg-white p-7 h-full relative">
                  <div className="absolute -top-5 -start-3 w-14 h-14 rounded-full bg-[#14C8B0] border-2 border-ink shadow-nb font-display font-black text-2xl text-white flex items-center justify-center">
                    {n}
                  </div>
                  <h3 className="mt-4 font-display font-black text-2xl leading-tight text-ink">
                    {t(`landing.how.step${n}.title`)}
                  </h3>
                  <p className="mt-3 text-[#3D424B] leading-relaxed">
                    {t(`landing.how.step${n}.body`)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FEATURE SPOTLIGHT 2: SAFETY & TRUST HUB ============== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="card-nb bg-white p-6 border-2 border-ink rounded-2xl">
              <div className="flex items-center gap-3 pb-4 border-b-2 border-ink">
                <ShieldCheck className="w-8 h-8 text-[#14C8B0]" strokeWidth={2.5} />
                <div>
                  <div className="font-display font-black text-xl text-ink">TANTA Safety Hub</div>
                  <div className="text-xs text-[#7C828D]">Verified Profile Shield</div>
                </div>
              </div>
              <div className="mt-5 space-y-3">
                <div className="p-3 bg-[#E8F7F2] border-2 border-ink rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#14C8B0] flex-shrink-0" />
                  <span className="text-sm font-bold text-ink">{t("landing.safetySection.badge1")}</span>
                </div>
                <div className="p-3 bg-[#FFFDF7] border-2 border-ink rounded-xl flex items-center gap-3">
                  <Star className="w-5 h-5 text-[#F9C349] flex-shrink-0" />
                  <span className="text-sm font-bold text-ink">{t("landing.safetySection.badge2")}</span>
                </div>
                <div className="p-3 bg-[#FFE6E0] border-2 border-ink rounded-xl flex items-center gap-3">
                  <Lock className="w-5 h-5 text-[#FF7A6B] flex-shrink-0" />
                  <span className="text-sm font-bold text-ink">{t("landing.safetySection.badge3")}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal>
              <span className="font-script text-3xl text-[#14C8B0]">
                {t("landing.safetySection.kicker")}
              </span>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
                {t("landing.safetySection.headline")}
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-6 text-base sm:text-lg text-[#3D424B] leading-relaxed max-w-xl">
                {t("landing.safetySection.body")}
              </p>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-8">
                <Link
                  to="/auth?mode=signup"
                  className="btn-nb btn-nb-primary text-base px-6 py-3.5 inline-flex items-center gap-2"
                  data-testid="landing-safety-signup-btn"
                >
                  {t("landing.cta.startFree")}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============== TRUST & SECURITY BAR ============== */}
      <section className="bg-[#0A0B0D] text-white py-16 lg:py-24 border-y-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[0.95] max-w-3xl">
              {t("landing.trust.headline")}
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, key: "verified" },
              { icon: CoinsIcon, key: "stripe" },
              { icon: Globe, key: "global" },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.key} delay={120 * i}>
                  <div className="border-2 border-white/30 rounded-2xl p-6 hover:border-[#14C8B0] transition-colors">
                    <Icon className="w-8 h-8 text-[#14C8B0]" strokeWidth={2.5} />
                    <h3 className="mt-4 font-display font-extrabold text-xl">{t(`landing.trust.${b.key}.title`)}</h3>
                    <p className="mt-2 text-white/70 text-sm leading-relaxed">{t(`landing.trust.${b.key}.body`)}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-10 flex flex-wrap gap-6 items-center font-mono text-xs uppercase tracking-widest text-white/60">
            <span>● Stripe Verified Payments</span>
            <span>● Google OAuth</span>
            <span>● ID Verification</span>
            <span>● GDPR Compliant</span>
            <span>● 10 Languages (RTL Ready)</span>
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 lg:py-32">
        <Reveal>
          <h2 className="font-display font-black text-5xl sm:text-7xl lg:text-[100px] leading-[0.88] tracking-tight text-ink">
            {t("landing.finalCta.line1")}
            <br />
            <span className="text-[#14C8B0]">{t("landing.finalCta.line2")}</span>
          </h2>
        </Reveal>
        <Reveal delay={150}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/auth?mode=signup"
              className="btn-nb btn-nb-primary text-lg px-8 py-4 inline-flex items-center gap-2"
              data-testid="landing-final-signup-btn"
            >
              {t("landing.cta.joinNow")}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/auth"
              className="btn-nb btn-nb-coral text-lg px-8 py-4"
              data-testid="landing-final-login-btn"
            >
              {t("auth.login")}
            </Link>
            <span className="font-script text-2xl text-[#FF7A6B] rotate-[-2deg]">
              {t("landing.finalCta.note")}
            </span>
          </div>
        </Reveal>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className="border-t-2 border-ink bg-[#0A0B0D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#14C8B0] border-2 border-white/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.8} />
            </div>
            <span className="font-display font-black text-2xl tracking-tight">TANTA</span>
            <span className="font-mono text-xs text-white/50">© 2026</span>
          </div>
          <div className="flex flex-wrap gap-5 text-sm text-white/70">
            <Link to="/legal" data-testid="footer-privacy">{t("landing.footer.privacy")}</Link>
            <Link to="/legal" data-testid="footer-terms">{t("landing.footer.terms")}</Link>
            <Link to="/guidelines" data-testid="footer-guidelines">{t("landing.footer.guidelines")}</Link>
            <a href="mailto:hello@meetup.app" data-testid="footer-contact">{t("landing.footer.contact")}</a>
            <Link to="/auth" data-testid="footer-login">{t("auth.login")}</Link>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        [dir="rtl"] .animate-\\[marquee_28s_linear_infinite\\] {
          animation-direction: reverse;
        }
      `}</style>
    </div>
  );
}
